import React, { useContext, useState, useEffect } from 'react';
import CardContainer from '@components/redesign/CardContainer';
import KpiGrid from '@components/redesign/KpiGrid';
import KpiTile from '@components/redesign/KpiTile';
import { KpiData } from '@components/redesign/KpiTile/types';
import { FilterContext } from '@components/FilterBar/FilterProvider';
import KpiDefinitionData from '../../../../../service/KpiDefinitionData';
import { useDynamicGraphQLQuery } from '@hooks/useDynamicGraphQLQuery';
import { useValueRealizationContext } from './ValueRealizationContext';
import totalHours from '@v2/kpis/mfg/ipc/summary/totalHours';
import agentRuntimeHours from '@v2/kpis/mfg/ipc/summary/agentRuntimeHours';
import agentRuntimeInPercentage from '@v2/kpis/mfg/ipc/summary/agentRuntimeInPercentage';
import wasteReduced from '@v2/kpis/mfg/ipc/summary/wasteReduced';
import wasteReduction from '@v2/kpis/mfg/ipc/summary/wasteReduction';
import hoursOptimized from '@v2/kpis/mfg/ipc/summary/hoursOptimized';
import avgNetThroughputIncrement from '@v2/kpis/mfg/ipc/summary/avgNetThroughputIncrement';

interface SummaryScorecardProps {
  isOverviewMode?: boolean;
}

const SummaryScorecard: React.FC<SummaryScorecardProps> = ({
  isOverviewMode = false,
}) => {
  const { selectedProduct, selectedPlant } = useContext(FilterContext);
  const { getProcessedFilters, isLoadingFilters } =
    useValueRealizationContext();
  const [overViewConfig, setOverViewConfig] = useState<KpiData[]>([]);

  // Get processed filter values based on current selections
  // Remove useMemo to ensure we always get fresh filter values
  const { lineValues, productTypeValues } = getProcessedFilters();

  // Use ['All'] values when in overview mode, otherwise use actual filter values
  const apiLineValues = isOverviewMode ? ['All'] : lineValues;
  const apiProductTypeValues = isOverviewMode ? ['All'] : productTypeValues;

  const { data, isLoading, setShouldFetch } = useDynamicGraphQLQuery(
    'getIPCVCSummaryMetrics',
    {
      line: apiLineValues,
      productType: apiProductTypeValues,
      siteId: selectedPlant?.id,
    },
    false,
    null,
    'network-only'
  );

  // Control API calls based on filter loading state
  useEffect(() => {
    if (
      !isLoadingFilters &&
      selectedPlant?.id &&
      apiLineValues &&
      apiProductTypeValues
    ) {
      setShouldFetch(true);
    }
  }, [
    isLoadingFilters,
    selectedPlant?.id,
    apiLineValues,
    apiProductTypeValues,
    setShouldFetch,
  ]);

  useEffect(() => {
    if (data?.getIPCVCSummaryMetrics?.data) {
      setOverViewConfig(
        generateScoreCardItems({ ...data.getIPCVCSummaryMetrics.data })
      );
    }
  }, [data, isLoading]);
  const generateScoreCardItems = (data: any) => {
    const items: KpiData[] = [
      totalHours.handler(data),
      agentRuntimeHours.handler(data),
      agentRuntimeInPercentage.handler(data),
      avgNetThroughputIncrement.handler(data),
      wasteReduction.handler(data),
      wasteReduced.handler(data),
      hoursOptimized.handler(data),
    ];
    return items;
  };

  return (
    <CardContainer
      title="Summary"
      isAccordion={false}
      showInfoIcon={true}
      dataDefinitions={{
        sections:
          KpiDefinitionData[selectedProduct?.productId]?.valueCreation
            ?.summary ?? [],
      }}
      isLoading={isLoading}
    >
      <KpiGrid
        columnsDesktop={4}
        columnsTablet={3}
        columnsMobile={2}
        showHDividerMobile={false}
      >
        {overViewConfig.map((item, index) => (
          <KpiTile key={index} data={item} />
        ))}
      </KpiGrid>
    </CardContainer>
  );
};

export default SummaryScorecard;
